import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { FaUpload, FaDownload, FaTrash, FaEye, FaEdit, FaTimes, FaSpinner, FaShare, FaCloudDownloadAlt, FaSave, FaBars, FaFileAlt, FaPhone, FaEnvelope, FaCalendar, FaPaperPlane, FaBan, FaPlus, FaChevronDown, FaCog } from 'react-icons/fa';
import { MdOutlineRefresh } from "react-icons/md";
import { useAuth } from '../../contexts/AuthContext';
import { usePlan } from '../../contexts/PlanContext';
import { useToast } from '../../contexts/ToastContext';
import { uploadContactsCSV, previewContactsCSV, deleteContact, importContactsFromHubSpot, getHubSpotContactsPreview, updateContact, getContactEngagements } from '../../services/radar';
import api from '../../services/axios';
import FieldMappingModal from './FieldMappingModal';

const RadarContacts = memo(({ 
  contacts = [],
  selectedCompany = '',
  onCompanyChange = null,
  contactSearchTerm = '',
  onContactSearchChange = null,
  loading = false,
  pagination = null,
  requestParams = {},
  onLoadContacts
}) => {
  const { user } = useAuth();
  const { planType } = usePlan();
  const { showError, showSuccess } = useToast();
  
  const [contactsFilters, setContactsFilters] = useState({
    source: '',
    company: selectedCompany
  });
  
  // Sorting state
  const [sorting, setSorting] = useState({
    primary: '',
    secondary: ''
  });
  
  // Sync filters with incoming props
  const buildContactLoadParams = useCallback((overrides = {}) => ({
    search: overrides.search ?? contactSearchTerm ?? '',
    source: overrides.source ?? contactsFilters.source ?? '',
    company: overrides.company ?? contactsFilters.company ?? '',
    page: overrides.page ?? requestParams.page ?? 1,
    limit: overrides.limit ?? requestParams.limit ?? 50
  }), [contactSearchTerm, contactsFilters, requestParams]);

  const reloadContacts = useCallback((overrides = {}) => {
    if (typeof onLoadContacts === 'function') {
      onLoadContacts(buildContactLoadParams(overrides));
    }
  }, [onLoadContacts, buildContactLoadParams]);

  useEffect(() => {
    setContactsFilters(prev => ({
      ...prev,
      source: requestParams.source ?? '',
      company: selectedCompany || requestParams.company || ''
    }));
  }, [requestParams?.source, requestParams?.company, selectedCompany]);

  useEffect(() => {
    const normalizedCompany = selectedCompany || '';
    if ((requestParams?.company ?? '') === normalizedCompany) return;
    reloadContacts({ company: normalizedCompany, page: 1 });
  }, [selectedCompany, requestParams?.company, reloadContacts]);

  useEffect(() => {
    const normalizedSearch = contactSearchTerm || '';
    if ((requestParams?.search ?? '') === normalizedSearch) return;
    reloadContacts({ search: normalizedSearch, page: 1 });
  }, [contactSearchTerm, requestParams?.search, reloadContacts]);

  // File upload state
  const [isContactsUploading, setIsContactsUploading] = useState(false);
  const [contactsSelectedFile, setContactsSelectedFile] = useState(null);
  const [contactsPreviewData, setContactsPreviewData] = useState(null);
  const [contactsValidationErrors, setContactsValidationErrors] = useState([]);
  const contactsFileInputRef = useRef(null);

  // Field mapping state
  const [showContactsFieldMapping, setShowContactsFieldMapping] = useState(false);
  const [contactsCsvHeaders, setContactsCsvHeaders] = useState([]);
  const [contactsCsvSampleRow, setContactsCsvSampleRow] = useState(null);
  const [contactsFieldMappings, setContactsFieldMappings] = useState({});
  const [contactsCustomFields, setContactsCustomFields] = useState({});

  // Import state
  const [isContactsHubSpotImporting, setIsContactsHubSpotImporting] = useState(false);
  const [showContactsHubSpotPreview, setShowContactsHubSpotPreview] = useState(false);
  const [contactsHubSpotPreviewData, setContactsHubSpotPreviewData] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [isLoadingContactsPreview, setIsLoadingContactsPreview] = useState(false);
  const [isContactsHubSpotPreviewUpdating, setIsContactsHubSpotPreviewUpdating] = useState(false);
  const [showContactsHubSpotFieldMapping, setShowContactsHubSpotFieldMapping] = useState(false);
  const [contactsHubSpotPreviewSearchTerm, setContactsHubSpotPreviewSearchTerm] = useState('');
  const [contactsHubSpotPreviewPage, setContactsHubSpotPreviewPage] = useState(1);
  const [contactsHubSpotPreviewTotalPages, setContactsHubSpotPreviewTotalPages] = useState(1);
  const contactsHubSpotCompaniesPerPage = 25;

  // Dropdown state
  const contactsDropdownRef = useRef(null);
  const [showAddContactsDropdown, setShowAddContactsDropdown] = useState(false);

  // LinkedIn ID editing state
  const [editingLinkedInId, setEditingLinkedInId] = useState(null); // contactId when editing LinkedIn ID
  const [linkedInIdValue, setLinkedInIdValue] = useState(''); // temp value for editing

  // Tab state
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts' or 'engagements'

  // Engagements state (for Contact Engagements tab)
  const [engagements, setEngagements] = useState([]);
  const [engagementsLoading, setEngagementsLoading] = useState(false);
  const [engagementsError, setEngagementsError] = useState('');

  // Error and success states - now handled by toast notifications

  // Load engagements when engagements tab is selected
  useEffect(() => {
    if (activeTab === 'engagements') {
      loadEngagements();
    }
  }, [activeTab, contactSearchTerm]);

  // Handle click outside Add Contacts dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddContactsDropdown && contactsDropdownRef.current) {
        if (!contactsDropdownRef.current.contains(event.target)) {
          setShowAddContactsDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAddContactsDropdown]);

  // Load engagements from new endpoint
  const loadEngagements = async () => {
    try {
      setEngagementsLoading(true);
      setEngagementsError('');
      
      const params = {
        search: contactSearchTerm,
        page: 1,
        limit: 100
      };
      
      console.log('🔍 Loading engagements with params:', params);
      const response = await getContactEngagements(params);
      console.log('📊 Engagements API response:', response);
      
      if (response.status === 'success') {
        setEngagements(response.engagements || []);
      } else {
        setEngagementsError(response.message || 'Failed to load engagements');
        setEngagements([]);
      }
    } catch (err) {
      console.error(' Error loading engagements:', err);
      setEngagementsError(err.message || 'Failed to load engagements');
      setEngagements([]);
    } finally {
      setEngagementsLoading(false);
    }
  };

  // Search and filter handlers
  const handleContactsSearch = (e) => {
    if (onContactSearchChange) {
      onContactSearchChange(e.target.value);
    }
  };

  const handleContactsFilterChange = (filter, value) => {
    setContactsFilters(prev => ({
      ...prev,
      [filter]: value
    }));
    if (filter === 'company' && onCompanyChange) {
      onCompanyChange({ target: { value } });
    }
    reloadContacts({ [filter]: value, page: 1 });
  };

  const clearContactsFilters = () => {
    if (onContactSearchChange) {
      onContactSearchChange('');
    }
    if (onCompanyChange) {
      onCompanyChange({ target: { value: '' } });
    }
    setContactsFilters({
      source: '',
      company: ''
    });
    reloadContacts({ search: '', source: '', company: '', page: 1 });
  };

  // Sorting function
  const sortContacts = (contactsToSort) => {
    if (!sorting.primary && !sorting.secondary) {
      return contactsToSort;
    }

    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    
    const getFieldValue = (contact, field) => {
      switch (field) {
        case 'firstName':
          return (contact.firstName || '').toString().trim().toLowerCase();
        case 'lastName':
          return (contact.lastName || '').toString().trim().toLowerCase();
        case 'email':
          return (contact.email || '').toString().trim().toLowerCase();
        case 'phone':
          return (contact.phone || '').toString().trim().toLowerCase();
        case 'company':
          return (contact.company || '').toString().trim().toLowerCase();
        case 'title':
          return (contact.title || '').toString().trim().toLowerCase();
        case 'source':
          return (contact.source || '').toString().trim().toLowerCase();
        default:
          return '';
      }
    };

    return [...contactsToSort].sort((a, b) => {
      // Primary sort
      if (sorting.primary) {
        const valueA = getFieldValue(a, sorting.primary);
        const valueB = getFieldValue(b, sorting.primary);
        const primaryComparison = collator.compare(valueA, valueB);
        
        if (primaryComparison !== 0) {
          return primaryComparison;
        }
      }

      // Secondary sort (only if primary values are equal)
      if (sorting.secondary) {
        const valueA = getFieldValue(a, sorting.secondary);
        const valueB = getFieldValue(b, sorting.secondary);
        return collator.compare(valueA, valueB);
      }

      return 0;
    });
  };

  // File upload handlers
  const handleContactsFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const input = event.target;
    setContactsSelectedFile(file);

    try {
      setIsLoadingContactsPreview(true);
      const previewResult = await previewContactsCSV(file);
      
      if (previewResult?.status === 'success' && previewResult.data) {
        const { headers, sampleRows } = previewResult.data;
        setContactsCsvHeaders(headers);
        setContactsCsvSampleRow(sampleRows && sampleRows.length > 0 ? sampleRows[0] : null);
        setShowContactsFieldMapping(true);
      } else {
        showError('Failed to preview CSV file');
      }
    } catch (error) {
      console.error('Preview error:', error);
      showError(error.message || 'Failed to preview CSV file');
    } finally {
      setIsLoadingContactsPreview(false);
      // Reset input so users can select the same file again if needed
      if (input) {
        input.value = '';
      }
    }
  };

  const handleContactsFieldMappingConfirm = async (mappingData) => {
    setContactsFieldMappings(mappingData.mappings);
    setContactsCustomFields(mappingData.customFields);
    setShowContactsFieldMapping(false);
    
    // Proceed with upload using the mappings
    await handleContactsUpload(contactsSelectedFile, mappingData.mappings, mappingData.customFields);
  };

  const handleContactsUpload = async (fileToUpload = contactsSelectedFile, mappings = contactsFieldMappings, customFieldsData = contactsCustomFields) => {
    if (!fileToUpload) {
      showError('Please select a CSV file to upload');
      return;
    }

    setIsContactsUploading(true);
    
    try {
      const response = await uploadContactsCSV(fileToUpload, mappings, customFieldsData);
      
      if (response && (Array.isArray(response.contacts) || response.message)) {
        setContactsSelectedFile(null);
        setContactsPreviewData(null);
        setContactsValidationErrors([]);
        setContactsFieldMappings({});
        setContactsCustomFields({});
        showSuccess('Contacts uploaded successfully');
        await reloadContacts();
      } else {
        throw new Error(response?.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Contacts upload error:', error);
      showError(error.message || 'Failed to upload contacts file');
    } finally {
      setIsContactsUploading(false);
    }
  };

  // Import handlers
  const handleContactsHubSpotImport = async () => {
    try {
      setIsLoadingContactsPreview(true);
      setContactsHubSpotPreviewSearchTerm('');
      setContactsHubSpotPreviewPage(1);
      
      // Determine mode based on active tab
      const mode = activeTab === 'engagements' ? 'engagements' : 'contacts';
      
      console.log('🔍 Fetching HubSpot preview with mode:', mode);
      const response = await getHubSpotContactsPreview(mode, mode === 'contacts'
        ? { page: 1, companiesPerPage: contactsHubSpotCompaniesPerPage, q: '' }
        : {}
      );
      console.log('📦 Raw API response:', response);
      
      // getHubSpotContactsPreview already returns response.data, so response IS the data
      const previewData = response;
      console.log('📊 Preview data:', previewData);
      
      if (mode === 'engagements') {
        // For engagements mode, store engagements array
        console.log('🎯 Engagements mode - checking previewData.engagements:', previewData.engagements);
        if (previewData.engagements && Array.isArray(previewData.engagements)) {
          console.log(` Found ${previewData.engagements.length} engagements`);
          setContactsHubSpotPreviewData({ 
            engagements: previewData.engagements, 
            total: previewData.total || previewData.engagements.length,
            mode: 'engagements'
          });
        } else {
          console.warn('⚠️ No engagements array found in response:', previewData);
          setContactsHubSpotPreviewData({ engagements: [], total: 0, mode: 'engagements' });
        }
      } else {
        // For contacts mode, store contacts array
        if (previewData.contacts && Array.isArray(previewData.contacts)) {
          setContactsHubSpotPreviewData({ 
            contacts: previewData.contacts, 
            total: previewData.total,
            mode: 'contacts'
          });
          setContactsHubSpotPreviewPage(previewData.page || 1);
          setContactsHubSpotPreviewTotalPages(previewData.totalPages || 1);
        } else if (Array.isArray(previewData)) {
          setContactsHubSpotPreviewData({ 
            contacts: previewData, 
            total: previewData.length,
            mode: 'contacts'
          });
          setContactsHubSpotPreviewPage(1);
          setContactsHubSpotPreviewTotalPages(1);
        } else {
          setContactsHubSpotPreviewData({ contacts: [], total: 0, mode: 'contacts' });
          setContactsHubSpotPreviewPage(1);
          setContactsHubSpotPreviewTotalPages(1);
        }
      }
      
      setSelectedContacts([]);
      setShowContactsHubSpotPreview(true);
    } catch (error) {
      console.error('HubSpot contacts preview error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get HubSpot contacts preview';
      showError(errorMessage);
    } finally {
      setIsLoadingContactsPreview(false);
    }
  };

  const fetchContactsHubSpotPreviewPage = async (pageToLoad, search = contactsHubSpotPreviewSearchTerm, { soft = false } = {}) => {
    try {
      if (soft) {
        setIsContactsHubSpotPreviewUpdating(true);
      } else {
        setIsLoadingContactsPreview(true);
      }
      const data = await getHubSpotContactsPreview('contacts', {
        page: pageToLoad,
        companiesPerPage: contactsHubSpotCompaniesPerPage,
        q: (search || '').trim()
      });
      setContactsHubSpotPreviewData({
        mode: 'contacts',
        contacts: Array.isArray(data?.contacts) ? data.contacts : [],
        total: data?.total || 0
      });
      setContactsHubSpotPreviewPage(data?.page || pageToLoad);
      setContactsHubSpotPreviewTotalPages(data?.totalPages || 1);
    } catch (err) {
      console.error('HubSpot contacts preview page fetch error:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load HubSpot contacts';
      showError(errorMessage);
    } finally {
      setIsLoadingContactsPreview(false);
      setIsContactsHubSpotPreviewUpdating(false);
    }
  };

  // Debounced server-side search for the preview overlay (contacts mode)
  useEffect(() => {
    if (!showContactsHubSpotPreview) return;
    if (contactsHubSpotPreviewData?.mode !== 'contacts') return;
    const t = setTimeout(() => {
      fetchContactsHubSpotPreviewPage(1, contactsHubSpotPreviewSearchTerm, { soft: true });
      setSelectedContacts([]);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsHubSpotPreviewSearchTerm, showContactsHubSpotPreview, contactsHubSpotPreviewData?.mode]);

  const handleContactsHubSpotImportConfirm = async () => {
    if (selectedContacts.length === 0) {
      showError(`Please select at least one ${contactsHubSpotPreviewData?.mode === 'engagements' ? 'engagement' : 'contact'} to import`);
      return;
    }

    // Show field mapping modal for HubSpot import (only for contacts mode, not engagements)
    if (contactsHubSpotPreviewData?.mode === 'contacts' && contactsHubSpotPreviewData?.contacts?.length > 0) {
      const firstContact = contactsHubSpotPreviewData.contacts[0];
      // Extract HubSpot properties
      const hubspotFields = firstContact.properties ? Object.keys(firstContact.properties) : [];
      if (hubspotFields.length > 0) {
        setContactsCsvHeaders(hubspotFields);
        setContactsCsvSampleRow(firstContact);
        setShowContactsHubSpotFieldMapping(true);
        return;
      }
    }

    // Proceed without mapping if engagements mode or no properties
    await proceedWithContactsHubSpotImport({}, {});
  };

  const handleContactsHubSpotFieldMappingConfirm = async (mappingData) => {
    setShowContactsHubSpotFieldMapping(false);
    await proceedWithContactsHubSpotImport(mappingData.mappings, mappingData.customFields);
  };

  const proceedWithContactsHubSpotImport = async (mappings = {}, customFieldsData = {}) => {
    try {
      setIsContactsHubSpotImporting(true);
      
      if (contactsHubSpotPreviewData?.mode === 'engagements') {
        // For engagements mode, import selected engagements
        const selectedEngagements = contactsHubSpotPreviewData.engagements.filter(e => 
          selectedContacts.includes(e.id)
        );
        
        // Group by contact ID for import
        const engagementsByContact = {};
        selectedEngagements.forEach(engagement => {
          const contactId = engagement.contactId;
          if (!engagementsByContact[contactId]) {
            engagementsByContact[contactId] = [];
          }
          engagementsByContact[contactId].push(engagement.engagementId);
        });
        
        // Import contacts and their engagements in one call
        const contactIds = Object.keys(engagementsByContact);
        const importResponse = await api.post('/contacts/import-hubspot', {
          selectedContacts: contactIds,
          selectedEngagements: engagementsByContact,
          fieldMappings: mappings,
          customFields: customFieldsData
        });
        
        setShowContactsHubSpotPreview(false);
        setContactsHubSpotPreviewData(null);
        setSelectedContacts([]);
        
        await reloadContacts();
        if (activeTab === 'engagements') {
          await loadEngagements();
        }
        
        showSuccess(`Successfully imported ${selectedEngagements.length} engagement${selectedEngagements.length !== 1 ? 's' : ''} from HubSpot`);
      } else {
        // For contacts mode, import selected contacts
        const response = await importContactsFromHubSpot(selectedContacts, mappings, customFieldsData);
        
        setShowContactsHubSpotPreview(false);
        setContactsHubSpotPreviewData(null);
        setSelectedContacts([]);
        
        await reloadContacts();
        showSuccess('Contacts imported successfully from HubSpot');
      }
    } catch (error) {
      console.error('HubSpot import error:', error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to import ${contactsHubSpotPreviewData?.mode === 'engagements' ? 'engagements' : 'contacts'} from HubSpot`;
      showError(errorMessage);
    } finally {
      setIsContactsHubSpotImporting(false);
    }
  };

  // Contact management handlers
  const handleDeleteContact = async (contactId) => {
    try {
      await deleteContact(contactId);
      await reloadContacts();
      showSuccess('Contact deleted successfully');
    } catch (err) {
      console.error('Error deleting contact:', err);
      showError('Failed to delete contact');
    }
  };

  const handleSaveLinkedInId = async (contactId) => {
    try {
      await updateContact(contactId, { linkedinId: linkedInIdValue });
      setEditingLinkedInId(null);
      setLinkedInIdValue('');
      await reloadContacts();
      showSuccess('LinkedIn ID updated successfully');
    } catch (err) {
      console.error('Error updating LinkedIn ID:', err);
      showError('Failed to update LinkedIn ID');
    }
  };

  const exportContacts = () => {
    const csvContent = [
      ['Name', 'Email', 'Phone', 'Company', 'Title', 'Source'],
      ...contacts.map(contact => [
        contact.name || '',
        contact.email || '',
        contact.phone || '',
        contact.company || '',
        contact.title || '',
        contact.source || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-contacts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Dropdown handlers
  const toggleAddContactsDropdown = () => {
    setShowAddContactsDropdown(!showAddContactsDropdown);
  };

  const closeAddContactsDropdown = () => {
    setShowAddContactsDropdown(false);
  };

  const handleUploadContacts = () => {
    closeAddContactsDropdown();
    if (contactsFileInputRef.current) {
      contactsFileInputRef.current.click();
    }
  };

  const handleContactsHubSpotImportClick = () => {
    closeAddContactsDropdown();
    handleContactsHubSpotImport();
  };

  const currentPage = pagination?.currentPage ?? requestParams.page ?? 1;
  const totalPages = pagination?.totalPages ?? (pagination?.totalItems && pagination?.itemsPerPage
    ? Math.ceil(pagination.totalItems / pagination.itemsPerPage)
    : null);
  const itemsPerPage = pagination?.itemsPerPage ?? requestParams.limit ?? 50;
  const canGoPrev = currentPage > 1;
  const canGoNext = totalPages ? currentPage < totalPages : contacts.length === itemsPerPage;

  // Sort HubSpot contacts in the preview overlay by Company, then Contact Name
  const sortedHubSpotPreviewContacts = (() => {
    const list = contactsHubSpotPreviewData?.contacts;
    if (!Array.isArray(list)) return [];

    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const normalize = (value) => (value ?? '').toString().trim();
    const isEmpty = (value) => normalize(value) === '';
    const getCompany = (c) => normalize(c?.company);
    const getName = (c) => normalize(`${c?.firstName ?? ''} ${c?.lastName ?? ''}`.trim());

    return [...list].sort((a, b) => {
      const companyA = getCompany(a);
      const companyB = getCompany(b);
      if (isEmpty(companyA) !== isEmpty(companyB)) return isEmpty(companyA) ? 1 : -1; // empty companies last
      if (!isEmpty(companyA) && !isEmpty(companyB)) {
        const companyCmp = collator.compare(companyA, companyB);
        if (companyCmp !== 0) return companyCmp;
      }

      const nameA = getName(a);
      const nameB = getName(b);
      if (isEmpty(nameA) !== isEmpty(nameB)) return isEmpty(nameA) ? 1 : -1; // empty names last
      if (!isEmpty(nameA) && !isEmpty(nameB)) {
        const nameCmp = collator.compare(nameA, nameB);
        if (nameCmp !== 0) return nameCmp;
      }

      // Deterministic tie-breaker
      return collator.compare(normalize(a?.id), normalize(b?.id));
    });
  })();

  const visibleHubSpotPreviewContactIds = sortedHubSpotPreviewContacts.map(c => c.id);
  const allVisibleHubSpotPreviewContactsSelected =
    visibleHubSpotPreviewContactIds.length > 0 &&
    visibleHubSpotPreviewContactIds.every(id => selectedContacts.includes(id));

  return (
    <div className="radar-content">
      <div style={{ display: 'flex', gap: '20px', height: '100%' }}>
        {/* Left Side - Filters (Company and Search moved to header, only Data Source remains) */}
        <div className="radar-contacts-left-pane" style={{ width: '250px', marginLeft: '20px', height: '80vh' }}>
          <div className="search-filters-section" style={{ display: 'flex', flexDirection: 'column', padding: '5px' }}>
            {/* Search Bar */}
            <div className="search-section" style={{ width: '250px' }}>
              <input
                type="text"
                value={contactSearchTerm || ''}
                onChange={handleContactsSearch}
                placeholder="Search contacts..."
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  marginBottom: '10px'
                }}
              />
              <button
                onClick={() => reloadContacts({ search: contactSearchTerm || '', page: 1 })}
                style={{
                  width: '100%',
                  padding: '8px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Search
              </button>
            </div>

            {/* Filtering Section */}
            <div className="filtering-section" style={{ width: '250px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <h3 style={{fontSize: '18px', fontWeight: '600', color: '#1f2937'}}>Filter</h3>
              <div className="filters" style={{ width: '250px'}}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Data Source</label>
                <select
                  value={contactsFilters.source}
                  onChange={(e) => handleContactsFilterChange('source', e.target.value)}
                  className="filter-select"
                  style={{ width: '100%', marginBottom: '15px' }}
                >
                  <option value="">All Sources</option>
                  <option value="hubspot">HubSpot</option>
                  <option value="csv_upload">CSV Upload</option>
                </select>
                <button onClick={() => {
                  clearContactsFilters();
                }} className="clear-filters-button" style={{ width: '100%' }}>
                  Clear Filters
                </button>
              </div>
            </div>
            
            {/* Sorting Section */}
            <div className="sorting-section" style={{ width: '100%', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>Sort</h3>
              <div className="sorting-controls" style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Primary Sort Field</label>
                  <select
                    value={sorting.primary}
                    onChange={(e) => setSorting(prev => ({ ...prev, primary: e.target.value }))}
                    className="sort-select"
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  >
                    <option value="">None</option>
                    <option value="firstName">First Name</option>
                    <option value="lastName">Last Name</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="company">Company</option>
                    <option value="title">Title</option>
                    <option value="source">Source</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>Secondary Sort Field</label>
                  <select
                    value={sorting.secondary}
                    onChange={(e) => setSorting(prev => ({ ...prev, secondary: e.target.value }))}
                    className="sort-select"
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  >
                    <option value="">None</option>
                    <option value="firstName">First Name</option>
                    <option value="lastName">Last Name</option>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="company">Company</option>
                    <option value="title">Title</option>
                    <option value="source">Source</option>
                  </select>
                </div>
                <button 
                  onClick={() => setSorting({ primary: '', secondary: '' })}
                  disabled={!sorting.primary && !sorting.secondary}
                  className="clear-sorting-button" 
                  style={{ 
                    width: '100%', 
                    padding: '8px', 
                    background: (!sorting.primary && !sorting.secondary) ? '#f3f4f6' : '#f3f4f6', 
                    color: (!sorting.primary && !sorting.secondary) ? '#9ca3af' : '#374151', 
                    border: '1px solid #d1d5db', 
                    borderRadius: '6px', 
                    cursor: (!sorting.primary && !sorting.secondary) ? 'not-allowed' : 'pointer', 
                    fontSize: '14px',
                    fontWeight: '500',
                    opacity: (!sorting.primary && !sorting.secondary) ? 0.6 : 1
                  }}
                >
                  Clear Sorting
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Contacts Table */}
        <div className="radar-contacts-right-pane" style={{ flex: 1, background: 'transparent', marginRight: '20px', height: '80vh'}}>
          <div className="radar-contacts-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>Team Contact List ({(pagination?.totalItems ?? contacts.length) || 0} entries)</h3>
            <div className="radar-contacts-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <button
                onClick={exportContacts}
                style={{ padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <FaDownload />
                Export
              </button>
              
              <div className="radar-contacts-dropdown" ref={contactsDropdownRef} style={{ position: 'relative' }}>
                <button
                  onClick={toggleAddContactsDropdown}
                  disabled={isLoadingContactsPreview}
                  style={{ 
                    padding: '8px 16px', 
                    background: isLoadingContactsPreview ? '#9ca3af' : '#3b82f6', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '6px', 
                    cursor: isLoadingContactsPreview ? 'not-allowed' : 'pointer', 
                    fontSize: '12px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    opacity: isLoadingContactsPreview ? 0.7 : 1
                  }}
                >
                  {isLoadingContactsPreview ? (
                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <FaPlus />
                  )}
                  Add Contacts
                  {!isLoadingContactsPreview && <FaChevronDown />}
                </button>
                
                {showAddContactsDropdown && (
                  <div style={{ position: 'absolute', top: '100%', right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', zIndex: 1000, minWidth: '200px' }}>
                    <button
                      onClick={handleUploadContacts}
                      style={{ width: '100%', padding: '8px 12px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}
                    >
                      <FaUpload />
                      Upload CSV
                    </button>
                    <button
                      onClick={handleContactsHubSpotImportClick}
                      disabled={isContactsHubSpotImporting || isLoadingContactsPreview}
                      style={{ 
                        width: '100%', 
                        padding: '8px 12px', 
                        textAlign: 'left', 
                        background: (isContactsHubSpotImporting || isLoadingContactsPreview) ? '#f3f4f6' : 'none', 
                        border: 'none', 
                        cursor: (isContactsHubSpotImporting || isLoadingContactsPreview) ? 'not-allowed' : 'pointer', 
                        fontSize: '12px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        opacity: (isContactsHubSpotImporting || isLoadingContactsPreview) ? 0.6 : 1,
                        color: (isContactsHubSpotImporting || isLoadingContactsPreview) ? '#9ca3af' : 'inherit'
                      }}
                    >
                      {(isContactsHubSpotImporting || isLoadingContactsPreview) ? (
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      ) : (
                        <FaCloudDownloadAlt />
                      )}
                      Import from HubSpot
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Hidden file input */}
          <input
            ref={contactsFileInputRef}
            type="file"
            accept=".csv"
            onChange={handleContactsFileSelect}
            style={{ display: 'none' }}
          />

          {/* Contacts Content */}
          <div className="radar-contacts-content" style={{ background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', width: '95%', height: '70vh', overflowY: 'auto', overflowX: 'auto' }}>
            {/* Tab Switch */}
            <div style={{ marginBottom: '20px', width: '100%'}}>
              <div style={{ display: 'flex', gap: '4px', background: '#f9fafb', borderRadius: '6px', padding: '8px' }}>
                <button
                  onClick={() => setActiveTab('contacts')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '500',
                    background: activeTab === 'contacts' ? '#1a6ed8' : 'transparent',
                    color: activeTab === 'contacts' ? 'white' : '#6b7280',
                    transition: 'all 0.2s'
                  }}
                >
                  Only Contacts
                </button>
                <button
                  onClick={() => setActiveTab('engagements')}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '500',
                    background: activeTab === 'engagements' ? '#1a6ed8' : 'transparent',
                    color: activeTab === 'engagements' ? 'white' : '#6b7280',
                    transition: 'all 0.2s'
                  }}
                >
                  Contact Engagements
                </button>
              </div>
            </div>
            {activeTab === 'contacts' ? (
              loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '16px' }}>
                <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px', color: '#3b82f6' }} />
                <p style={{ margin: '10px 0 0 0', color: '#6b7280' }}>Loading contacts...</p>
              </div>
            ) : contacts.length > 0 ? (
              <div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>First Name</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Last Name</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Email</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Phone</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Company</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Title</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>LinkedIn ID</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Source</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortContacts(contacts).map((contact) => (
                        <tr key={contact.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                          {contact.firstName || 'N/A'}
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                          {contact.lastName || 'N/A'}
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                          {contact.email || 'N/A'}
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                          {contact.phone || 'N/A'}
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                          {contact.company || 'N/A'}
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                          {contact.title || 'N/A'}
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px', color: '#374151', width: '240px' }}>
                          {editingLinkedInId === contact.id ? (
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center'}}>
                              <input
                                type="text"
                                value={linkedInIdValue}
                                onChange={(e) => setLinkedInIdValue(e.target.value)}
                                placeholder="https://www.linkedin.com/in/tanyabisht/"
                                style={{
                                  padding: '4px 8px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  width: '140px'
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveLinkedInId(contact.id);
                                  } else if (e.key === 'Escape') {
                                    setEditingLinkedInId(null);
                                    setLinkedInIdValue('');
                                  }
                                }}
                                autoFocus
                              />
                              <button
                                onClick={() => handleSaveLinkedInId(contact.id)}
                                style={{
                                  padding: '2px 6px',
                                  background: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '10px'
                                }}
                                title="Save"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => {
                                  setEditingLinkedInId(null);
                                  setLinkedInIdValue('');
                                }}
                                style={{
                                  padding: '2px 6px',
                                  background: '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '10px'
                                }}
                                title="Cancel"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <span>
                                {contact.linkedinId 
                                  ? `linkedin.com/in/${contact.linkedinId}`
                                  : 'N/A'
                                }
                              </span>
                              <button
                                onClick={() => {
                                  setEditingLinkedInId(contact.id);
                                  setLinkedInIdValue(contact.linkedinId || '');
                                }}
                                style={{
                                  padding: '2px 4px',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '10px',
                                  color: '#3b82f6'
                                }}
                                title="Edit LinkedIn ID"
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '8px', fontSize: '12px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '500',
                            background: contact.source === 'hubspot' ? '#dcfce7' : contact.source === 'salesforce' ? '#fef3c7' : '#f3f4f6',
                            color: contact.source === 'hubspot' ? '#166534' : contact.source === 'salesforce' ? '#92400e' : '#374151'
                          }}>
                            {contact.source || 'manual'}
                          </span>
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleDeleteContact(contact.id)}
                              style={{
                                padding: '4px',
                                background: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '10px'
                              }}
                              title="Delete Contact"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalPages && totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => reloadContacts({ page: currentPage - 1 })}
                        disabled={!canGoPrev}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: canGoPrev ? '#fff' : '#f3f4f6',
                          cursor: canGoPrev ? 'pointer' : 'not-allowed',
                          fontSize: '12px'
                        }}
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => reloadContacts({ page: currentPage + 1 })}
                        disabled={!canGoNext}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          background: canGoNext ? '#fff' : '#f3f4f6',
                          cursor: canGoNext ? 'pointer' : 'not-allowed',
                          fontSize: '12px'
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                <FaFileAlt style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>No contacts found</h3>
                <p style={{ margin: '0', fontSize: '14px' }}>
                  {contactSearchTerm || Object.values(contactsFilters).some(f => f !== '') 
                    ? 'Try adjusting your search or filters' 
                    : 'Add your first contact to get started'
                  }
                </p>
              </div>
            )
            ) : (
              /* Contact Engagements Tab */
              <div>
                {engagementsLoading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', gap: '16px' }}>
                    <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px', color: '#3b82f6' }} />
                    <p style={{ margin: '10px 0 0 0', color: '#6b7280' }}>Loading engagements...</p>
                  </div>
                ) : engagementsError ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#ef4444' }}>
                    <p style={{ margin: '0', fontSize: '14px' }}>{engagementsError}</p>
                  </div>
                ) : engagements.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <FaFileAlt style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                    <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>No engagements found</h3>
                    <p style={{ margin: '0', fontSize: '14px' }}>
                      Import engagements from HubSpot to see them here
                    </p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Contact</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Company</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Type</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Date</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Subject</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Details</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {engagements.map((engagement) => {
                          const engagementData = engagement.engagementData || {};
                          const engagementType = engagement.engagementType || engagementData.type || 'UNKNOWN';
                          const createdAt = engagement.createdAt || engagement.engagementCreatedAt || engagementData.createdAt;
                          const subject = engagementData.subject || engagement.engagementSubject || '';
                          const body = engagementData.body || engagement.engagementBody || '';
                          const contactName = engagement.contactName || 
                                            (engagement.contact ? `${engagement.contact.firstName || ''} ${engagement.contact.lastName || ''}`.trim() : '') ||
                                            'Unknown Contact';
                          const contactEmail = engagement.contactEmail || engagement.contact?.email || '';
                          const contactCompany = engagement.contact?.companyName || engagement.contactCompany || '';
                          const source = engagement.source || 'unknown';
                          
                          // Format date
                          const engagementDate = createdAt 
                            ? new Date(createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })
                            : 'N/A';
                          
                          // Format engagement type display
                          const getTypeDisplay = (type) => {
                            const typeMap = {
                              'CALL': 'Call',
                              'EMAIL': 'Email',
                              'MEETING': 'Meeting',
                              'NOTE': 'Note',
                              'TASK': 'Task'
                            };
                            return typeMap[type] || type || 'Unknown';
                          };
                          
                          // Get engagement preview text
                          const getEngagementPreview = () => {
                            if (subject) return subject;
                            if (body) {
                              const preview = body.substring(0, 50);
                              return preview + (body.length > 50 ? '...' : '');
                            }
                            return engagementType === 'CALL' ? 'Phone call' : 
                                   engagementType === 'EMAIL' ? 'Email sent' :
                                   engagementType === 'MEETING' ? 'Meeting scheduled' :
                                   engagementType === 'NOTE' ? 'Note added' :
                                   engagementType === 'TASK' ? 'Task created' : 'No details';
                          };
                          
                          return (
                            <tr key={engagement.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                                <div>
                                  <div style={{ fontWeight: '500' }}>{contactName}</div>
                                  {contactEmail && (
                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{contactEmail}</div>
                                  )}
                                </div>
                              </td>
                              <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                                {contactCompany || 'N/A'}
                              </td>
                              <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                                <span style={{
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '500',
                                  background: engagementType === 'CALL' ? '#dbeafe' : 
                                             engagementType === 'EMAIL' ? '#fef3c7' :
                                             engagementType === 'MEETING' ? '#e0e7ff' :
                                             engagementType === 'NOTE' ? '#f3f4f6' :
                                             engagementType === 'TASK' ? '#dcfce7' : '#f3f4f6',
                                  color: engagementType === 'CALL' ? '#1e40af' :
                                         engagementType === 'EMAIL' ? '#92400e' :
                                         engagementType === 'MEETING' ? '#4338ca' :
                                         engagementType === 'NOTE' ? '#374151' :
                                         engagementType === 'TASK' ? '#166534' : '#374151'
                                }}>
                                  {getTypeDisplay(engagementType)}
                                </span>
                              </td>
                              <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                                {engagementDate}
                              </td>
                              <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                                {subject || 'N/A'}
                              </td>
                              <td style={{ padding: '8px', fontSize: '12px', color: '#374151', maxWidth: '300px' }}>
                                <div style={{ 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap',
                                  title: body || subject || 'No details'
                                }}>
                                  {getEngagementPreview()}
                                </div>
                              </td>
                              <td style={{ padding: '8px', fontSize: '12px' }}>
                                <span style={{
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: '500',
                                  background: source === 'hubspot' ? '#dcfce7' : source === 'salesforce' ? '#fef3c7' : '#f3f4f6',
                                  color: source === 'hubspot' ? '#166534' : source === 'salesforce' ? '#92400e' : '#374151'
                                }}>
                                  {source || 'manual'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Error and Success Messages - Now handled by toast notifications in top right corner */}
        </div>

        {/* HubSpot Contacts Preview Modal */}
        {showContactsHubSpotPreview && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => {
              setShowContactsHubSpotPreview(false);
              setContactsHubSpotPreviewData(null);
              setSelectedContacts([]);
              setContactsHubSpotPreviewSearchTerm('');
            }}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '8px',
                padding: '24px',
                width: '90%',
                maxWidth: '800px',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#374151' }}>
                  {contactsHubSpotPreviewData?.mode === 'engagements' 
                    ? 'Import Contact Engagements from HubSpot' 
                    : 'Import Contacts from HubSpot'}
                </h3>
                <button
                  onClick={() => {
                    setShowContactsHubSpotPreview(false);
                    setContactsHubSpotPreviewData(null);
                    setSelectedContacts([]);
                    setContactsHubSpotPreviewSearchTerm('');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: 0,
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Close"
                >
                  ✕
                </button>
              </div>

              {isLoadingContactsPreview && !contactsHubSpotPreviewData ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px', gap: '16px' }}>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '32px', color: '#3b82f6' }} />
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                    {contactsHubSpotPreviewData?.mode === 'engagements' 
                      ? 'Loading HubSpot engagements...' 
                      : 'Loading HubSpot contacts...'}
                  </p>
                </div>
              ) : contactsHubSpotPreviewData?.mode === 'engagements' ? (
                /* ENGAGEMENTS MODE - One row per engagement */
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px' }}>
                      Select the engagements you want to import ({contactsHubSpotPreviewData.engagements?.length || 0} available)
                    </p>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                      <button
                        onClick={() => {
                          if (selectedContacts.length === contactsHubSpotPreviewData.engagements?.length) {
                            setSelectedContacts([]);
                          } else {
                            setSelectedContacts(contactsHubSpotPreviewData.engagements.map(e => e.id));
                          }
                        }}
                        style={{
                          padding: '4px 12px',
                          background: '#f3f4f6',
                          color: '#374151',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        {selectedContacts.length === contactsHubSpotPreviewData.engagements?.length ? 'Deselect All' : 'Select All'}
                      </button>
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>
                        {selectedContacts.length} selected
                      </span>
                    </div>
                  </div>

                  <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', width: '40px' }}>
                            <input
                              type="checkbox"
                              checked={selectedContacts.length === contactsHubSpotPreviewData.engagements?.length && contactsHubSpotPreviewData.engagements?.length > 0}
                              onChange={() => {
                                if (selectedContacts.length === contactsHubSpotPreviewData.engagements?.length) {
                                  setSelectedContacts([]);
                                } else {
                                  setSelectedContacts(contactsHubSpotPreviewData.engagements.map(e => e.id));
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          </th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Contact</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Company</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Type</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Date</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contactsHubSpotPreviewData.engagements?.map((engagement) => (
                          <tr key={engagement.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="checkbox"
                                checked={selectedContacts.includes(engagement.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedContacts([...selectedContacts, engagement.id]);
                                  } else {
                                    setSelectedContacts(selectedContacts.filter(id => id !== engagement.id));
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              <div>
                                <div style={{ fontWeight: '500' }}>{engagement.contactName}</div>
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>{engagement.contactEmail}</div>
                              </div>
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              {engagement.contactCompany || 'N/A'}
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              {engagement.engagementType}
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              {engagement.engagementDate}
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={engagement.engagementDisplay || engagement.engagementSubject || 'N/A'}>
                                {engagement.engagementDisplay || engagement.engagementSubject || 'N/A'}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button
                      onClick={() => {
                        setShowContactsHubSpotPreview(false);
                        setContactsHubSpotPreviewData(null);
                        setSelectedContacts([]);
                        setContactsHubSpotPreviewSearchTerm('');
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleContactsHubSpotImportConfirm}
                      disabled={selectedContacts.length === 0 || isContactsHubSpotImporting}
                      style={{
                        padding: '8px 16px',
                        background: selectedContacts.length === 0 || isContactsHubSpotImporting ? '#d1d5db' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: selectedContacts.length === 0 || isContactsHubSpotImporting ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {isContactsHubSpotImporting ? (
                        <>
                          <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                          Importing...
                        </>
                      ) : (
                        `Import ${selectedContacts.length} Engagement${selectedContacts.length !== 1 ? 's' : ''}`
                      )}
                    </button>
                  </div>
                </>
              ) : contactsHubSpotPreviewData && contactsHubSpotPreviewData.contacts ? (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ margin: '0 0 12px 0', color: '#6b7280', fontSize: '14px' }}>
                      Select the contacts you want to import (Page {contactsHubSpotPreviewPage} of {contactsHubSpotPreviewTotalPages})
                    </p>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                      <input
                        type="text"
                        value={contactsHubSpotPreviewSearchTerm}
                        onChange={(e) => setContactsHubSpotPreviewSearchTerm(e.target.value)}
                        placeholder="Search contacts (name, email, company, title)"
                        style={{
                          flex: 1,
                          padding: '8px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '12px'
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => {
                            if (allVisibleHubSpotPreviewContactsSelected) {
                              setSelectedContacts(prev => prev.filter(id => !visibleHubSpotPreviewContactIds.includes(id)));
                            } else {
                              setSelectedContacts(prev => Array.from(new Set([...prev, ...visibleHubSpotPreviewContactIds])));
                            }
                          }}
                          style={{
                            padding: '4px 12px',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {allVisibleHubSpotPreviewContactsSelected ? 'Deselect All' : 'Select All'}
                        </button>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>
                          {selectedContacts.length} selected
                        </span>
                      </div>

                      {/* Pagination controls (right aligned) */}
                      {contactsHubSpotPreviewTotalPages > 1 && (
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => fetchContactsHubSpotPreviewPage(Math.max(1, contactsHubSpotPreviewPage - 1), contactsHubSpotPreviewSearchTerm, { soft: true })}
                            disabled={contactsHubSpotPreviewPage <= 1 || isLoadingContactsPreview || isContactsHubSpotPreviewUpdating}
                            style={{
                              padding: '6px 10px',
                              background: contactsHubSpotPreviewPage <= 1 || isLoadingContactsPreview || isContactsHubSpotPreviewUpdating ? '#e5e7eb' : '#f3f4f6',
                              color: '#374151',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: contactsHubSpotPreviewPage <= 1 || isLoadingContactsPreview || isContactsHubSpotPreviewUpdating ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Prev
                          </button>
                          {Array.from({ length: contactsHubSpotPreviewTotalPages }, (_, i) => i + 1)
                            .filter(p => p === 1 || p === contactsHubSpotPreviewTotalPages || Math.abs(p - contactsHubSpotPreviewPage) <= 2)
                            .reduce((acc, p, idx, arr) => {
                              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                              acc.push(p);
                              return acc;
                            }, [])
                            .map((p, idx) => (
                              p === '…' ? (
                                <span key={`ellipsis-${idx}`} style={{ fontSize: '12px', color: '#6b7280', padding: '0 4px' }}>…</span>
                              ) : (
                                <button
                                  key={`page-${p}`}
                                  onClick={() => fetchContactsHubSpotPreviewPage(p, contactsHubSpotPreviewSearchTerm, { soft: true })}
                                  disabled={isLoadingContactsPreview || isContactsHubSpotPreviewUpdating}
                                  style={{
                                    padding: '6px 10px',
                                    background: p === contactsHubSpotPreviewPage ? '#3b82f6' : '#f3f4f6',
                                    color: p === contactsHubSpotPreviewPage ? 'white' : '#374151',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: isLoadingContactsPreview || isContactsHubSpotPreviewUpdating ? 'not-allowed' : 'pointer',
                                    fontSize: '12px'
                                  }}
                                >
                                  {p}
                                </button>
                              )
                            ))}
                          <button
                            onClick={() => fetchContactsHubSpotPreviewPage(Math.min(contactsHubSpotPreviewTotalPages, contactsHubSpotPreviewPage + 1), contactsHubSpotPreviewSearchTerm, { soft: true })}
                            disabled={contactsHubSpotPreviewPage >= contactsHubSpotPreviewTotalPages || isLoadingContactsPreview || isContactsHubSpotPreviewUpdating}
                            style={{
                              padding: '6px 10px',
                              background: contactsHubSpotPreviewPage >= contactsHubSpotPreviewTotalPages || isLoadingContactsPreview || isContactsHubSpotPreviewUpdating ? '#e5e7eb' : '#f3f4f6',
                              color: '#374151',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: contactsHubSpotPreviewPage >= contactsHubSpotPreviewTotalPages || isLoadingContactsPreview || isContactsHubSpotPreviewUpdating ? 'not-allowed' : 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ position: 'relative', maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                    {isContactsHubSpotPreviewUpdating && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(255, 255, 255, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 5
                      }}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '22px', color: '#3b82f6' }} />
                      </div>
                    )}
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151', width: '40px' }}>
                            <input
                              type="checkbox"
                              checked={allVisibleHubSpotPreviewContactsSelected}
                              onChange={() => {
                                if (allVisibleHubSpotPreviewContactsSelected) {
                                  setSelectedContacts(prev => prev.filter(id => !visibleHubSpotPreviewContactIds.includes(id)));
                                } else {
                                  setSelectedContacts(prev => Array.from(new Set([...prev, ...visibleHubSpotPreviewContactIds])));
                                }
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          </th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Name</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Email</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Company</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Title</th>
                          <th style={{ padding: '8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedHubSpotPreviewContacts.map((contact) => (
                          <tr key={contact.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <td style={{ padding: '8px' }}>
                              <input
                                type="checkbox"
                                checked={selectedContacts.includes(contact.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedContacts([...selectedContacts, contact.id]);
                                  } else {
                                    setSelectedContacts(selectedContacts.filter(id => id !== contact.id));
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              {contact.firstName} {contact.lastName}
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              {contact.email || 'N/A'}
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              {contact.company || 'N/A'}
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px', color: '#374151' }}>
                              {contact.jobTitle || 'N/A'}
                            </td>
                            <td style={{ padding: '8px', fontSize: '12px' }}>
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '500',
                                background: contact.action === 'update' ? '#fef3c7' : '#dcfce7',
                                color: contact.action === 'update' ? '#92400e' : '#166534'
                              }}>
                                {contact.action === 'update' ? 'Update' : 'New'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button
                      onClick={() => {
                        setShowContactsHubSpotPreview(false);
                        setContactsHubSpotPreviewData(null);
                        setSelectedContacts([]);
                        setContactsHubSpotPreviewSearchTerm('');
                      }}
                      style={{
                        padding: '8px 16px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleContactsHubSpotImportConfirm}
                      disabled={selectedContacts.length === 0 || isContactsHubSpotImporting}
                      style={{
                        padding: '8px 16px',
                        background: selectedContacts.length === 0 || isContactsHubSpotImporting ? '#d1d5db' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: selectedContacts.length === 0 || isContactsHubSpotImporting ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {isContactsHubSpotImporting ? (
                        <>
                          <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                          Importing...
                        </>
                      ) : (
                        `Import ${selectedContacts.length} Contact${selectedContacts.length !== 1 ? 's' : ''}`
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  <p style={{ margin: 0, fontSize: '14px' }}>No contacts found or failed to load preview</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Field Mapping Modal for CSV Upload */}
      <FieldMappingModal
        isOpen={showContactsFieldMapping}
        onClose={() => {
          setShowContactsFieldMapping(false);
          setContactsSelectedFile(null);
          setContactsCsvHeaders([]);
          setContactsCsvSampleRow(null);
        }}
        onConfirm={handleContactsFieldMappingConfirm}
        incomingFields={contactsCsvHeaders}
        existingFields={[]}
        sourceType="csv"
      />

      {/* Field Mapping Modal for HubSpot Import */}
      <FieldMappingModal
        isOpen={showContactsHubSpotFieldMapping}
        onClose={() => {
          setShowContactsHubSpotFieldMapping(false);
          setContactsCsvHeaders([]);
          setContactsCsvSampleRow(null);
        }}
        onConfirm={handleContactsHubSpotFieldMappingConfirm}
        incomingFields={contactsCsvHeaders}
        existingFields={[]}
        sourceType="hubspot"
      />
    </div>
  );
});

export default RadarContacts;

RadarContacts.displayName = 'RadarContacts';
